import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, deDE } from '@mui/x-data-grid';
import { Add } from '@mui/icons-material';
import { UserGroupEdit } from './UserGroupEdit';
import { useGetUserGroups, useGetUsers } from '../../../hooks/data';
import { LoadingSpinner } from '../../../core/LoadingSpinner';
import { IUserGroup, isSystemUserGroup } from '../../../Interfaces/IUserGroup';
import { IUser } from '../../../Interfaces/IUser';

const columns: GridColDef[] = [
  {
    field: 'idUserGroup',
    headerName: 'Nr.',
    width: 90,
  },
  {
    field: 'Email',
    headerName: 'E-Mail',
    width: 250,
  },
  {
    field: 'PhoneNumber',
    headerName: 'Rufnummer',
    width: 250,
  },
  {
    field: 'UserGroup',
    headerName: 'Gruppe',
    flex: 1,
  },
];

export const UserGroupOverview = () => {
  const { userGroups, isLoadingUserGroups } = useGetUserGroups();
  const { users, isLoadingUsers } = useGetUsers();

  if (isLoadingUserGroups || isLoadingUsers) {
    return <LoadingSpinner />;
  }

  if (!userGroups || !users) {
    return <Alert severity='error'>Fehler!</Alert>;
  }

  return <UserGroup initialUserGroups={userGroups} users={users} />;
};

/**
 * Gruppenverwaltung mit zwei Reitern: die normalen Benutzergruppen für die
 * organisatorische Zuordnung, und daneben die Systemgruppen, die eine
 * Sonderfunktion steuern und deshalb anderen Regeln folgen.
 */
export const UserGroup = ({
  initialUserGroups,
  users,
}: {
  initialUserGroups: IUserGroup[];
  users: IUser[];
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userGroups, setUserGroups] = useState<IUserGroup[]>(initialUserGroups);
  const [idUserGroup, setIdUserGroup] = useState<number | null>(null);
  const [tab, setTab] = useState(0);

  const normalGroupArray = userGroups.filter(
    userGroup => !isSystemUserGroup(userGroup)
  );
  const systemGroupArray = userGroups.filter(userGroup =>
    isSystemUserGroup(userGroup)
  );

  // Sprung aus einer anderen Ansicht (z. B. E-Mail-Einstellungen →
  // Signatur-Uebersicht): navigate('/admin/groups', { state: { idUserGroup } })
  // oeffnet die Gruppe direkt. Der State wird danach verworfen.
  useEffect(() => {
    const requestedIdUserGroup = (
      location.state as { idUserGroup?: number } | null
    )?.idUserGroup;
    if (typeof requestedIdUserGroup === 'number') {
      setIdUserGroup(requestedIdUserGroup);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  return (
    <>
      <Dialog
        open={idUserGroup !== null}
        onClose={() => setIdUserGroup(null)}
        maxWidth='md'
        fullWidth
      >
        <DialogContent>
          {idUserGroup !== null && (
            <UserGroupEdit
              idUserGroup={idUserGroup}
              setIdUserGroup={setIdUserGroup}
              userArray={users}
              userGroupArray={userGroups}
              setUserGroupArray={setUserGroups}
            />
          )}
        </DialogContent>
      </Dialog>

      <Box sx={{ m: 2, mt: 0 }}>
        <Tabs
          value={tab}
          onChange={(_event, value: number) => setTab(value)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label='Benutzergruppen' />
          <Tab label='Systemgruppen' />
        </Tabs>

        {tab === 0 && (
          <>
            <Typography variant='h5'>
              Benutzergruppen
              <IconButton
                sx={{ float: 'right' }}
                onClick={() => setIdUserGroup(-1)}
              >
                <Add />
              </IconButton>
            </Typography>

            <DataGrid
              localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
              rows={normalGroupArray}
              columns={columns}
              getRowId={row => row.idUserGroup}
              onRowClick={params => setIdUserGroup(Number(params.id))}
              autoHeight
            />
          </>
        )}

        {tab === 1 && (
          <>
            <Typography variant='h5'>Systemgruppen</Typography>

            <Alert severity='info' sx={{ mt: 1, mb: 2 }}>
              Systemgruppen werden vom System bereitgestellt und steuern
              besondere Funktionen — etwa die Benachrichtigung der
              GwG-Verantwortlichen. Sie können deren Mitglieder wie gewohnt
              pflegen; Bezeichnung und Zweck sind fest vorgegeben. Eine
              Systemgruppe lässt sich weder löschen noch einem Vorgang, einer
              Aufgabe, einer Maßnahme oder einer Zuständigkeit zuordnen —
              deshalb erscheint sie dort auch nicht zur Auswahl.
            </Alert>

            {systemGroupArray.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                Es ist keine Systemgruppe vorhanden.
              </Typography>
            ) : (
              <DataGrid
                localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
                rows={systemGroupArray}
                columns={columns}
                getRowId={row => row.idUserGroup}
                onRowClick={params => setIdUserGroup(Number(params.id))}
                autoHeight
              />
            )}
          </>
        )}
      </Box>
    </>
  );
};
